import React, { useContext, useState } from 'react';
import { AppContext } from './App';
import './AddInvites.css';
import axios from 'axios';
import { SERVER_URL } from './config';
import * as XLSX from 'xlsx';

const AddInvites = () => {
  const [invites, setInvites] = useState([{ name: '', phone: '', relation: 'אחר' }]);
  const { user } = useContext(AppContext);

  const canPickContacts = !!(navigator.contacts && navigator.contacts.select);

  const flattenTel = (tel) => {
    if (!tel) return [];
    if (Array.isArray(tel)) return tel.flatMap(flattenTel);
    if (typeof tel === "string") return [{ value: tel, labels: [], pref: false }];

    const value = tel.number ?? tel.value ?? tel.tel ?? tel.phoneNumber ?? "";
    const rawLabels = []
      .concat(tel.type ?? [])
      .concat(tel.types ?? [])
      .concat(tel.label ?? [])
      .concat(tel.category ?? [])
      .concat(tel.kind ?? []);
    const labels = rawLabels.flat().map(x => String(x).toLowerCase());
    const pref = !!(tel.pref || tel.primary || tel.isPrimary || tel?.metadata?.primary);

    return [{ value, labels, pref }];
  };

  const onlyDigits = (s) => (s || "").toString().replace(/\D/g, "");

  const cleanPhone = (raw) => {
    let v = String(raw || "").trim();
    v = v.replace(/[^\d+]/g, "");
    v = v.replace(/(?!^)\+/g, "");
    return v.slice(0, 20);
  };

  const toPlainString = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return "";
    return String(Math.trunc(v)); 
  }
  return String(v);
};

const normalizeIsraelPhoneFront = (raw) => {
  const s = toPlainString(raw).trim();

  if (s.startsWith("+") || s.startsWith("00")) {
    return cleanPhone(s);
  }

  const digits = onlyDigits(s);

  if (digits.length === 10 && digits.startsWith("0")) {
    return digits;
  }
  
  return cleanPhone(s);
};


  const normKey = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^\p{L}\p{N}]+/gu, "");

  const HEADER_SYNONYMS = {
    name: new Set(["שם", "שם פרטי", "שם מלא", "name", "שמות", "אורחים", "contact name"].map(normKey)),
    phone: new Set(["מספר טלפון", "טלפון", "פלאפון", "נייד", "phone", "מספר הטלפון", "מספרים", "cellphone", "phone number"].map(normKey)),
    relation: new Set(["סוג קשר", "קשר", "קרבה", "relation", "relationship", "type", "סוג", "סוג הקשר"].map(normKey)),
  };

  const softMatch = (token, bag) => {
    const t = normKey(token);
    if (!t) return false;
    if (bag.has(t)) return true;
    for (const k of bag) if (t.includes(k) || k.includes(t)) return true;
    return false;
  };

  const looksLikeHeaderRow = (row) => {
    if (!row) return false;
    const cells = row.map((c) => String(c || ""));
    if (cells.filter((c) => c.trim()).length < 2) return false;
    const hits =
      (cells.some((c) => softMatch(c, HEADER_SYNONYMS.name)) ? 1 : 0) +
      (cells.some((c) => softMatch(c, HEADER_SYNONYMS.phone)) ? 1 : 0) +
      (cells.some((c) => softMatch(c, HEADER_SYNONYMS.relation)) ? 1 : 0);
    return hits >= 1;
  };

  const mapHeaderColumns = (row) => {
    const map = { name: -1, phone: -1, relation: -1 };
    row.forEach((cell, idx) => {
      const s = String(cell || "");
      if (map.name === -1 && softMatch(s, HEADER_SYNONYMS.name)) map.name = idx;
      else if (map.phone === -1 && softMatch(s, HEADER_SYNONYMS.phone)) map.phone = idx;
      else if (map.relation === -1 && softMatch(s, HEADER_SYNONYMS.relation)) map.relation = idx;
    });
    return map;
  };

  const KNOWN_RELATIONS = [
    "חברים חתן",
    "חברים כלה",
    "משפחה חתן",
    "משפחה כלה",
    "חברים הורים",
    "אחר",
  ];

  // הערה חשובה:
  // השרת דורש relation קיים ולא ריק. אם באקסל יש ערך מוזר/לא מוכר,
  // עדיף להפוך ל"אחר" מאשר לשלוח משהו שעלול להכשיל.
  const normalizeRelation = (raw) => {
    const r = String(raw || "").trim();
    return KNOWN_RELATIONS.includes(r) ? r : "אחר";
  };

  const guessColumnsWithoutHeader = (rows) => {
    const sample = rows.slice(0, Math.min(rows.length, 5));
    const colsCount = sample.reduce((m, r) => Math.max(m, r.length), 0);

    const scorePhone = Array(colsCount).fill(0);
    const scoreName = Array(colsCount).fill(0);
    const scoreRelation = Array(colsCount).fill(0);

    for (const r of sample) {
      for (let i = 0; i < colsCount; i++) {
        const raw = (r[i] ?? "").toString();
        const v = raw.trim();
        if (!v) continue;

        const cleaned = cleanPhone(v);
        const digits = onlyDigits(cleaned);

        const looksIntl =
          cleaned.startsWith("+") ||
          cleaned.startsWith("00") ||
          cleaned.startsWith("972") ||
          cleaned.startsWith("33") ||
          cleaned.startsWith("44") ||
          cleaned.startsWith("1") ||
          cleaned.startsWith("61") ||
          cleaned.startsWith("41") ||
          cleaned.startsWith("32");

        if (digits.length >= 8 || looksIntl) scorePhone[i] += looksIntl ? 5 : 1;
        if (/[A-Za-z\u0590-\u05FF]/.test(v) && digits.length <= 3) scoreName[i] += 2;
        if (KNOWN_RELATIONS.includes(v)) scoreRelation[i] += 10;
      }
    }

    const argMax = (arr) => arr.reduce((iMax, x, i, a) => (x > a[iMax] ? i : iMax), 0);

    const phoneIdx = argMax(scorePhone);

    let nameIdx = argMax(scoreName);
    if (nameIdx === phoneIdx) {
      const sorted = scoreName
        .map((v, i) => ({ v, i }))
        .sort((a, b) => b.v - a.v)
        .map((x) => x.i);
      nameIdx = sorted.find((i) => i !== phoneIdx) ?? nameIdx;
    }

    let relationIdx = argMax(scoreRelation);
    if (relationIdx === phoneIdx || relationIdx === nameIdx || scoreRelation[relationIdx] === 0) {
      relationIdx = -1;
    }

    return { name: nameIdx, phone: phoneIdx, relation: relationIdx };
  };

  const parseSheetFlexible = (sheet) => {
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });
    const cleanRows = rows.filter((r) => (r || []).some((c) => String(c || "").trim() !== ""));
    if (cleanRows.length === 0) return [];

    let headerRowIdx = [-1, 0, 1, 2].find(
      (idx) => idx >= 0 && idx < cleanRows.length && looksLikeHeaderRow(cleanRows[idx])
    );
    if (headerRowIdx === undefined) headerRowIdx = -1;

    let colMap;
    let dataStartIdx = 0;

    if (headerRowIdx >= 0) {
      colMap = mapHeaderColumns(cleanRows[headerRowIdx]);
      dataStartIdx = headerRowIdx + 1;
    } else {
      colMap = guessColumnsWithoutHeader(cleanRows);
      dataStartIdx = 0;
    }

    const out = [];
    for (let i = dataStartIdx; i < cleanRows.length; i++) {
      const row = cleanRows[i] || [];
      const nameRaw = colMap.name >= 0 ? row[colMap.name] : "";
      const phoneRaw = colMap.phone >= 0 ? row[colMap.phone] : "";
      const relRaw = colMap.relation >= 0 ? row[colMap.relation] : "";

      const name = String(nameRaw || "").trim();
      const phone = cleanPhone(phoneRaw || "");
      const relation = normalizeRelation(relRaw);

      if (!name && !phone) continue;

      out.push({ name, phone, relation });
    }
    return out;
  };

  const isTrulyEmpty = (r) => {
    const nameEmpty = !String(r.name || "").trim();
    const phoneEmpty = !String(r.phone || "").trim();
    const rel = String(r.relation || "").trim();
    const relationEmpty = rel === "" || rel === "אחר";
    return nameEmpty && phoneEmpty && relationEmpty;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const parsed = parseSheetFlexible(sheet)
          .map((row) => ({
            name: (row.name || "").trim(),
            phone: normalizeIsraelPhoneFront(row.phone || ""),
            relation: normalizeRelation(row.relation || "אחר"),
          }))
          .filter((r) => r.name || r.phone);

        console.log("[Excel import] parsed rows:", parsed.length);
        console.table(parsed.slice(0, 20));

        setInvites((prev) => [...prev.filter((r) => !isTrulyEmpty(r)), ...parsed]);

        // מאפשר להעלות שוב את אותו קובץ (אחרת onChange לפעמים לא יורה)
        event.target.value = "";
      } catch (err) {
        console.error("Error parsing Excel:", err);
        alert("הייתה בעיה בקריאת הקובץ. נסו לשמור כ-.xlsx מחדש ולעלות שוב.");
      }
    };

    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      alert("שגיאה בקריאת הקובץ.");
    };
  };

  const chooseMobileFromTel = (tel) => {
    const MOBILE = ["mobile", "cell", "cellular", "iphone", "android", "smartphone", "gsm", "נייד", "סלולרי", "פלאפון", "phone-mobile", "mobile-phone"];
    const WORK = ["work", "office", "business", "משרד", "עסקי", "עבודה", "phone-work", "work-phone"];
    const HOME = ["home", "בית", "פרטי", "phone-home", "home-phone"];
    const FAX = ["fax", "פקס"];

    const items = flattenTel(tel)
      .map((it, idx) => {
        const num = cleanPhone(it.value);
        const digitsLen = onlyDigits(num).length;
        if (!num || digitsLen < 8) return null;

        const labels = it.labels;
        const has = (patterns) => labels.some(l => patterns.some(p => l.includes(p)));

        let score = 1;
        if (has(MOBILE)) score += 1000;
        if (has(WORK)) score -= 50;
        if (has(HOME)) score -= 60;
        if (has(FAX)) score -= 500;
        if (it.pref) score += 10;

        return { num, score, isMobile: has(MOBILE), idx };
      })
      .filter(Boolean);

    if (!items.length) return "";

    items.sort((a, b) => b.score - a.score || (b.isMobile - a.isMobile) || a.idx - b.idx);
    return items[0].num;
  };

  const importContacts = async () => {
    if (!canPickContacts) {
      alert("ייבוא אנשי קשר מהמכשיר נתמך רק ב-Chrome באנדרואיד.");
      return;
    }
    try {
      const contacts = await navigator.contacts.select(["name", "tel"], { multiple: true });

      const formatted = contacts
        .map((c) => {
          const name = Array.isArray(c.name) ? (c.name[0] || "") : (c.name || "");
          const telCandidates = [c.tel, c.phoneNumbers, c.phones].filter(Boolean).flat();
          const phone = chooseMobileFromTel(telCandidates.length ? telCandidates : c.tel);
          return { name, phone, relation: "אחר" };
        })
        .filter((x) => x.phone);

      const normalized = formatted
        .map(x => ({
          name: (x.name || "").trim(),
          phone: normalizeIsraelPhoneFront(x.phone || ""),
          relation: "אחר",
        }))
        .filter(x => x.name || x.phone);

      setInvites((prev) => [
        ...prev.filter((r) => !isTrulyEmpty(r)),
        ...normalized,
      ]);
    } catch (err) {
      console.error("Error picking contacts:", err);
    }
  };

  const isFormValid = () => {
    return invites.every(invite => invite.name && invite.phone && invite.relation);
  };

  // מקבץ שגיאות מהשרת לפי סוג (לפי invite.error שהשרת מחזיר בפועל)
  const buildErrorSummary = (failedInvites) => {
    const groups = new Map(); // error -> names[]
    for (const inv of (failedInvites || [])) {
      const err = (inv && inv.error) ? String(inv.error) : "שגיאה לא ידועה";
      const nm = (inv && inv.name) ? String(inv.name) : "(ללא שם)";
      if (!groups.has(err)) groups.set(err, []);
      groups.get(err).push(nm);
    }

    const lines = [];
    for (const [err, names] of groups.entries()) {
      lines.push(`• ${err}: ${names.join(", ")}`);
    }
    return lines.join("\n");
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("נא למלא את כל התאים");
      return;
    }

    const cleanedInvites = invites.map(inv => ({
      ...inv,
      name: (inv.name || "").trim(),
      phone: normalizeIsraelPhoneFront(inv.phone || ""),
      relation: normalizeRelation(inv.relation || "אחר"),
    }));

    console.log("[add_invites] sending invites:", cleanedInvites.length);
    console.table(cleanedInvites.slice(0, 20));

    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${SERVER_URL}add_invites`,
        { invites: cleanedInvites },
        { headers: { Authorization: `Token ${token}` } }
      );

      // חשוב: גם partial_success הוא 207 (עדיין נכנס ל-try), לכן צריך לוג פה.
      console.log("[add_invites] status:", response.status);
      console.log("[add_invites] response.data:", response.data);

      const { status: serverStatus, failed_invites, successful_count } = response.data;

      if (serverStatus === 'success') {
        alert("נוסף בהצלחה ✅");
        setInvites([{ name: '', phone: '', relation: 'אחר' }]);
        return;
      }

      if (serverStatus === 'partial_success') {
        // כאן אנחנו מציגים הודעה *לפי השגיאה האמיתית* שהשרת החזיר (invite.error)
        console.warn("[add_invites] partial_success", { successful_count, failed_invites });

        // נציג בטבלה רק את הכושלים כדי שהמשתמש יוכל לתקן ולשמור שוב
        setInvites(
          (failed_invites || []).map(inv => ({
            name: (inv.name || "").trim(),
            phone: cleanPhone(inv.phone || ""),
            relation: normalizeRelation(inv.relation || "אחר"),
            error: inv.error, // נשמר לצורך דיבוג/תצוגה אם תרצי בעתיד
          }))
        );

        const summary = buildErrorSummary(failed_invites);
        alert(
          `חלק מהאורחים נוספו וחלק לא.\n` +
          `נוספו בהצלחה: ${successful_count}\n` +
          `לא נוספו: ${(failed_invites || []).length}\n\n` +
          `סיבות :\n${summary}\n\n`
        );
        return;
      }

      // אם השרת החזיר משהו לא צפוי
      console.warn("[add_invites] unexpected serverStatus:", serverStatus, response.data);
      alert("שגיאה לא צפויה");
    } catch (error) {
      // כאן נגיע כשזה 400/500 (למשל חריגה ממכסה / קלט לא תקין / תקלה)
      console.error('[add_invites] axios error:', error);

      const statusCode = error?.response?.status;
      const serverMsg = error?.response?.data?.error;

      // הערה: השרת שלך מחזיר {"error": "..."} ב-400/500, אז זו ההודעה הנכונה להציג.
      if (statusCode && serverMsg) {
        console.error('[add_invites] server response:', error.response.data);
        alert(serverMsg);
        return;
      }

      alert("הייתה שגיאה בלתי צפויה. נא לנסות שוב");
    }
  };

  const handleInputChange = (index, field, value) => {
  if (field === "phone") value = normalizeIsraelPhoneFront(value);

    const updatedInvites = [...invites];
    updatedInvites[index][field] = value;
    setInvites(updatedInvites);
  };

  const addRow = () => {
    setInvites([...invites, { name: '', phone: '', relation: 'אחר' }]);
  };

  const removeRow = (index) => {
    setInvites(invites.filter((_, i) => i !== index));
  };

  const openPopup = () => {
    const rows = prompt('כמה שורות ברצונך להוסיף');
    if (rows && !isNaN(rows) && Number(rows) > 0) {
      const newRows = Array.from({ length: Number(rows) }, () => ({
        name: '',
        phone: '',
        relation: 'אחר',
      }));
      setInvites([...invites, ...newRows]);
    }
  };

  return (
    <div className="invite-form">
      <div className="import-contacts-section">
        <button
          type="button"
          className="import-contacts-button"
          onClick={importContacts}
        >
          ייבא אנשי קשר מהמכשיר
        </button>

        <p className="import-note">
          הערה: ייבוא אנשי קשר מהמכשיר נתמך רק ב-Chrome באנדרואיד (אי אפשר להשתמש מהמחשב או מאייפון).
        </p>
      </div>

      <div className="instructions">
        <p>📌 להעלאת אורחים באמצעות קובץ אקסל לחצו כאן:</p>
        <input
          type="file"
          accept=".xlsx, .xls, .csv"
          onChange={handleFileUpload}
          className="file-upload"
        />

        <p className="note">
          ⚠️ שימו לב, ניתן להעלות קובץ אחד בכל הוספת אורחים.<br />
          אם ברצונכם להעלות יותר מאחד, לחצו <b>'שמירה'</b> ולאחר מכן תעלו את הקובץ הבא.<br />
          📌 יש לוודא ששמות ראשי העמודות באקסל הם: שם, מספר טלפון, סוג קשר
        </p>
      </div>

      {user?.general_invite_slug && (
        <div className="general-link-section">
          <p className="general-link-description">
            📎 ניתן לשתף את הקישור הבא בקבוצות (למשל WhatsApp) כדי לאפשר לאורחים להירשם בעצמם:
          </p>
          <div className="general-link-box">
            <input
              type="text"
              readOnly
              value={`https://mariage-il.com/invitation/general/${user.general_invite_slug}`}
              onClick={(e) => e.target.select()}
              className="general-link-input"
            />
            <button
              className="copy-link-button"
              onClick={() => {
                navigator.clipboard.writeText(`https://mariage-il.com/invitation/general/${user.general_invite_slug}`);
                alert("הקישור הועתק ללוח!");
              }}
            >
              העתק קישור
            </button>
            <a
              className="whatsapp-share-button"
              href={`https://wa.me/?text=${encodeURIComponent(
                "💌 היי! הנה הקישור להרשמה לחתונה שלנו: " +
                `https://mariage-il.com/invitation/general/${user.general_invite_slug}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              שיתוף ב־WhatsApp
            </a>
          </div>
        </div>
      )}

      <table className="invite-form-table">
        <thead>
          <tr>
            <th>שם</th>
            <th>מספר טלפון</th>
            <th>סוג קשר</th>
            <th>פעולות</th>
          </tr>
        </thead>
        <tbody>
          {invites.map((invite, index) => (
            <tr key={index}>
              <td>
                <input
                  type="text"
                  placeholder="הכנס שם"
                  value={invite.name}
                  onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="הכנס מספר טלפון"
                  value={invite.phone}
                  onChange={(e) => handleInputChange(index, "phone", e.target.value)}
                />
              </td>
              <td>
                <select
                  value={invite.relation}
                  onChange={(e) => handleInputChange(index, 'relation', e.target.value)}
                >
                  <option value="">בחר סוג קשר</option>
                  <option value="חברים חתן">חברים חתן</option>
                  <option value="חברים כלה">חברים כלה</option>
                  <option value="חברים הורים">חברים הורים</option>
                  <option value="משפחה חתן">משפחה חתן</option>
                  <option value="משפחה כלה">משפחה כלה</option>
                  <option value="אחר">אחר</option>
                </select>
              </td>
              <td>
                <button className="remove-row-button" onClick={() => removeRow(index)}>
                  הסר
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="button-row">
        <button className="add-row-button" onClick={addRow}>
          הוסף שורה
        </button>
        <button className="add-multiple-rows-button" onClick={openPopup}>
          הוסף מספר שורות
        </button>
        <button className="submit-button" onClick={handleSubmit}>
          שמירה
        </button>
      </div>
    </div>
  );
};

export default AddInvites;
